import React, { useMemo } from 'react';
import { useReimbursementScreen } from './hooks/useReimbursementScreen';

import { ReimbursementPolicyDetails } from './ReimbursementPolicyDetails';
import { ReimbursementMethodSection } from './ReimbursementMethodSection';
import {
  ReimbursementMultibrandList,
} from './multibrandList/ReimbursementMultibrandList';

import FigoLottie from '../../common/FigoLottie';
import loadingSpinner from '../../../assets/lottieFiles/loading-blue.json';

import { MY_ACCOUNT, POLICY_STATUS } from '../../../constants';

import './css/ReimbursementScreen.css';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';
import { ReimbursementDetailsFrame } from './reimbursementDetails';
import { useRecaptcha } from '../../../hooks';
import { useRecaptchaValidator } from '../../../hooks/useRecaptchaValidator';

const ReimbursementScreen = ({ breadCrumbs, currentFolderId }) => {
  const {
    allBrandsData,
    loadingAllBrandsData,
    onClickReimbursementCard,
    reimbursementOption,
  } = useReimbursementScreen({ breadCrumbs });
  const isReimDetails = currentFolderId === MY_ACCOUNT.reimbursementDetails;

  const currentPolicy = useMemo(() => breadCrumbs.navData.find((data) => data.id
    === currentFolderId).extraData?.policyInfo, [breadCrumbs]);

  const statusId = useMemo(() => {
    if (!currentPolicy?.PolicyNumber) {
      return POLICY_STATUS.unPolicy;
    }

    const data = allBrandsData?.find(({
      PolicyNumber,
    }) => PolicyNumber === currentPolicy.PolicyNumber);

    return data?.Status || POLICY_STATUS.unPolicy;
  }, [currentPolicy]);

  const source = useMemo(() => {
    if (breadCrumbs && breadCrumbs.navData && breadCrumbs.navData.length) {
      const myAccountSource = breadCrumbs.navData.some((data) => data.id
        === MY_ACCOUNT.reimbursement);

      return myAccountSource ? 'my account' : 'insurance';
    }

    return '';
  }, [breadCrumbs]);

  const needRecaptcha = useRecaptcha();
  const { recaptchaValidate, loading } = useRecaptchaValidator();

  if (loadingAllBrandsData || loading) {
    return (
      <div className="Reimbursement-screen-shimmer-container">
        <FigoLottie
          animationData={loadingSpinner}
          height={50}
          width={50}
        />
      </div>
    );
  }

  function renderContent() {
    if (needRecaptcha && !recaptchaValidate) {
      return null;
    }

    if (isReimDetails) {
      const isOrcaPolicy = currentPolicy && currentPolicy.MarketingChannelId
        !== MARKETING_CHANNEL_ID.figo;

      if (isOrcaPolicy) {
        return (
          <ReimbursementDetailsFrame
            petId={currentPolicy?.policyInfo?.Pets?.[0]?.PetId}
            policy={{
              coveragePeriodId: currentPolicy?.PolicyData?.CoveragePeriodId,
              marketingChannelId: currentPolicy?.PolicyData?.MarketChannelId,
              policyId: currentPolicy?.PolicyData?.Id,
              policyNumber: currentPolicy?.PolicyData?.PolicyDisplayNumber,
            }}
          />
        );
      }

      // Figo policy
      return (
        <div className="Reimbursement-screen-container">
          <ReimbursementPolicyDetails
            policyNumber={currentPolicy.PolicyNumber}
            policyPets={currentPolicy.Pets.map((pet) => pet.PetName).join(', ')}
            policyStatus={currentPolicy.PolicyStatus}
            policyStatusId={statusId}
            policyTerm={currentPolicy.PolicyTerm}
          />

          <ReimbursementMethodSection
            isRedirectAvailable={statusId === POLICY_STATUS.active
               || statusId === POLICY_STATUS.future}
            marketChannelId={currentPolicy.MarketingChannelId}
            policyNumber={currentPolicy.PolicyNumber}
            reimbursementOption={reimbursementOption}
            source={source}
          />
        </div>
      );
    }

    // both multibrand but no details screen (PolicySummaries data)?
    return (
      <ReimbursementMultibrandList
        allBrandsData={allBrandsData}
        breadCrumbs={breadCrumbs}
        onClickCard={onClickReimbursementCard}
      />
    );
  }

  return renderContent();
};

export { ReimbursementScreen };
